import _jsx from "@babel/runtime/helpers/jsx";
/* global googletag */
import qs from 'querystring';
import React, { useEffect, useRef, useState, createContext, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectScript } from '@atc/dom-fns';
import { usePrevious } from '@atc/use-previous';
import { getConsumerInsights } from '@atc/consumer-insights';
import _isEqual from 'lodash/isEqual';
import isObject from 'isobject';
import APSUtils from '../APSUtils';

/**
 *  The DFPProvider handles the loading of the DFP engine and processing the page level targeting and the ad request iteslf.
 *  It also utilizes context so that the AdSlot component dependency can push to the adSlots object and refresh the ads, specifically for lazy-loading.
 *  The children are required and the adUnit prop is needed if none are supplied by the AdUnit child(ren).
 **/

const noop = () => {};
let pubmaticFirstInject = false;
const checkTargetingDifference = (_ref, _ref2) => {
  let {
    ...firstTargeting
  } = _ref;
  let {
    ...secondTargeting
  } = _ref2;
  const differences = {};
  Object.keys(firstTargeting).forEach(targeting => {
    if (firstTargeting[targeting] !== secondTargeting[targeting]) {
      differences[targeting] = [firstTargeting[targeting], secondTargeting[targeting]];
    }
    delete firstTargeting[targeting];
    delete secondTargeting[targeting];
  });
  Object.keys(secondTargeting).forEach(targeting => {
    if (targeting) {
      differences[targeting] = ['', secondTargeting[targeting]];
    }
  });
  return differences;
};
export const DFPContext = /*#__PURE__*/createContext({
  addAdSlot: slot => {},
  addUnRefreshedAdCount: () => {},
  refreshAds: _ref3 => {
    let {
      isLazy,
      slotId
    } = _ref3;
  },
  clearAdSlots: () => {},
  adUnit: undefined,
  addToLazyLoadedAds: slot => {},
  addToRenderedSlots: slot => {},
  removeFromRenderedSlots: slot => {},
  prebidAdapters: []
});
function DFPProvider(_ref4) {
  let {
    adUnit,
    children,
    delay = 0,
    enableAdLibrariesLoading = true,
    enableLazyLoad = false,
    headerBidding = false,
    openWrapUrl,
    lazyLoadConfig = {
      fetchMarginPercent: 200,
      // Fetch slots within 2 viewports.
      renderMarginPercent: 100,
      // Render slots within 1 viewports.
      mobileScaling: 2.0 // Double the above values on mobile.
    },
    loadGPT = true,
    loadPrebid = false,
    onSlotRenderEnded,
    pageLevelTargeting = {},
    personalization,
    prebidAdapters = [],
    prebidDebug = false,
    prebidPath = '//www.autotrader.com/0_00_0000/resources/js/bundle/prebid.js',
    prebidTimeout = 2000,
    prodHost = 'www.autotrader.com',
    targetingChangeWithoutRefreshAds = []
  } = _ref4;
  const adSlots = useRef([]);
  const unRefreshedAdCount = useRef(0);
  const renderedSlots = useRef([]);
  const renderedLazySlots = useRef([]);
  const oldPageLevelTargetingProp = usePrevious(pageLevelTargeting) || pageLevelTargeting;
  const [isAdsScriptLoaded, setAdsScriptLoaded] = useState(false);
  const loadAdLibraries = useCallback(async () => {
    const protocol = document.location.protocol === 'http:' ? 'http:' : 'https:';
    const gptScriptSrc = `${protocol}//securepubads.g.doubleclick.net/tag/js/gpt.js`;

    // OpenWrap must load before GPT if it is being used.
    if (openWrapUrl && pubmaticFirstInject === false) {
      const profileVersionId = qs.parse(window.location.search.slice(1)).pwtv;
      const pwtVersionPath = profileVersionId ? '/' + profileVersionId : '';
      await injectScript(openWrapUrl + pwtVersionPath + '/pwt.js', {
        rejectOnError: true,
        delay
      }).catch(noop);
    }
    const loadPrebidPromise = loadPrebid ? injectScript(prebidPath, {
      delay
    }) : Promise.resolve();
    Promise.all([injectScript(gptScriptSrc, {
      delay
    }), loadPrebidPromise]).then(() => {
      if (headerBidding) {
        APSUtils.init();
      }
      setAdsScriptLoaded(true);
    });
  }, [delay, headerBidding, loadPrebid, openWrapUrl, prebidPath]);
  const getOptimizelyState = optimizely => {
    try {
      // get('state') methods belong to third-party which is out of control
      if (optimizely && optimizely.get('state')) {
        return optimizely.get('state');
      }
    } catch (err) {
      return undefined;
    }
    return undefined;
  };
  const getFirstCampaignKey = data => isObject(data) ? Object.keys(data)[0] : undefined;
  const getTidValue = useCallback(() => {
    let tidValue = '';
    if (typeof window === 'undefined') {
      return tidValue;
    }
    try {
      const {
        optimizelyEdge,
        optimizely
      } = window;
      const optimizelyState = getOptimizelyState(optimizely);
      const optimizelyEdgeState = getOptimizelyState(optimizelyEdge);
      if (optimizelyState) {
        const data = optimizelyState.getCampaignStates({
          isActive: true
        });
        const firstCampaignKey = getFirstCampaignKey(data);
        tidValue = firstCampaignKey ? optimizelyState.getDecisionString({
          campaignId: firstCampaignKey,
          shouldCleanString: true
        }) : '';
      }
      if (!tidValue && optimizelyEdgeState) {
        const edgeData = optimizelyEdgeState.getActiveExperiments();
        const firstCampaignKey = getFirstCampaignKey(edgeData);
        const data = firstCampaignKey ? edgeData[firstCampaignKey] : undefined;
        tidValue = data && data.variation ? data.id + ':' + data.variation.id : '';
      }
      return tidValue.replace(/[()]/g, '');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error from getTidValue():', err);
    }
    return tidValue;
  }, []);
  const getPageLevelTargeting = useCallback(_ref5 => {
    let {
      ...targeting
    } = _ref5;
    const isProd = window.location.host === prodHost;
    // All requests on non-Prod should be treated as anomalous
    // (i.e. treated the same as bot traffic)
    targeting.anml = !isProd ? 'y' : targeting.anml;

    // fetch and inject consumer insights data
    const consumerInsights = {
      ...getConsumerInsights(),
      ...personalization
    };
    window.googletag.pubads().clearTargeting();
    Object.entries({
      ...targeting,
      ...consumerInsights
    }).forEach(_ref6 => {
      let [key, value] = _ref6;
      window.googletag.pubads().setTargeting(key, value);
    });
    const tidValue = getTidValue();
    if (tidValue) {
      window.googletag.pubads().setTargeting('tid', tidValue);
    }
  }, [getTidValue, personalization, prodHost]);
  const initGPT = useCallback(() => {
    window.googletag.cmd.push(() => {
      if (pageLevelTargeting) {
        getPageLevelTargeting(pageLevelTargeting);
      }
      window.googletag.pubads().disableInitialLoad();
      window.googletag.pubads().enableAsyncRendering();
      window.googletag.pubads().enableSingleRequest();
      window.googletag.pubads().collapseEmptyDivs();
      if (enableLazyLoad) {
        googletag.pubads().enableLazyLoad(lazyLoadConfig);
      }
      if (pageLevelTargeting?.pixall_visitorid) {
        window.googletag.pubads().setPublisherProvidedId(pageLevelTargeting.pixall_visitorid);
      }
      window.googletag.enableServices();
    });

    // Send all bids into DFP
    window.pbjs.que.push(() => {
      // window.pbjs.enableSendAllBids();
      window.pbjs.setConfig({
        enableSendAllBids: true,
        debug: prebidDebug,
        bidderTimeout: prebidTimeout
      });
    });
  }, [enableLazyLoad, getPageLevelTargeting, lazyLoadConfig, pageLevelTargeting, prebidDebug, prebidTimeout]);
  const handleSlotRenderEnded = useCallback(event => {
    const {
      slot
    } = event;
    const getContainer = () => document.getElementById(slot?.getSlotElementId())?.closest('div.dfp-slot');
    const addClass = (element, className) => {
      if (element.classList && !element.classList.contains(className)) {
        element.classList.add(className);
      } else if (element.className.indexOf(className) > -1) {
        // eslint-disable-next-line no-unused-expressions
        `${element.className} ${className}`;
      }
    };
    const container = getContainer();
    if (container) {
      if (!event.isEmpty) {
        addClass(container, 'ad-rendered');
        if (container.style) {
          container.style.maxWidth = event && event.size[0] > 0 ? event.size[0] + 'px' : '100%';
        }
      } else {
        addClass(container, 'ad-empty');
      }
    }
    if (onSlotRenderEnded) {
      onSlotRenderEnded(event);
    }
  }, [onSlotRenderEnded]);
  const fetchBidsAndRefreshSlots = useCallback(slots => {
    if (headerBidding) {
      return APSUtils.fetchHeaderBids({
        slots,
        parallelBidding: loadPrebid,
        prebidAdapters
      });
    }
    return googletag.cmd.push(() => {
      if (slots && slots.length > 0) {
        window.googletag.pubads().refresh(slots);
      }
    });
  }, [headerBidding, loadPrebid, prebidAdapters]);
  const addAdSlot = useCallback(slot => {
    adSlots.current.push(slot);
  }, []);
  const addUnRefreshedAdCount = useCallback(() => {
    unRefreshedAdCount.current += 1;
  }, []);
  const refreshAds = useCallback(_ref7 => {
    let {
      isLazy,
      slotId
    } = _ref7;
    let result = false;
    if (window.googletag && unRefreshedAdCount.current > 0) {
      if (unRefreshedAdCount.current === adSlots.current.length) {
        unRefreshedAdCount.current = 0;
        if (!isLazy || !renderedLazySlots.current.includes(slotId)) {
          result = fetchBidsAndRefreshSlots(adSlots.current);
          renderedSlots.current = renderedSlots.current.concat(adSlots.current);
        }
        adSlots.current = [];
      } else {
        // console.error('unrefreshedAdCount ERROR', `${unRefreshedAdCount.current} != ${adSlots.current.length}`);
      }
    }
    return result;
  }, [fetchBidsAndRefreshSlots]);
  const clearAdSlots = useCallback(() => {
    if (window.googletag) {
      let result = false;
      window.googletag.cmd.push(() => {
        if (adSlots.current && adSlots.current.length > 0) {
          result = window.googletag.pubads().destroySlots(adSlots.current);
        }
      });
      unRefreshedAdCount.current = 0;
      adSlots.current = [];
      return result;
    }
    return false;
  }, []);
  const addToLazyLoadedAds = useCallback(slot => {
    if (slot) {
      const slotId = slot.getSlotElementId();
      const newRenderedSlots = [];
      renderedSlots.current.forEach(renderedSlot => {
        if (renderedSlot.getSlotElementId() !== slotId) {
          newRenderedSlots.push(renderedSlot);
        }
      });
      if (renderedLazySlots.current.indexOf(slotId) === -1) {
        renderedLazySlots.current.push(slotId);
      }
      renderedSlots.current = newRenderedSlots;
    }
  }, []);
  const addToRenderedSlots = useCallback(slot => {
    if (slot) {
      const slotElementId = slot.getSlotElementId();
      const newRenderedSlotIds = [];
      renderedSlots.current.forEach(renderedSlot => {
        newRenderedSlotIds.push(renderedSlot.getSlotElementId());
      });
      if (!newRenderedSlotIds.includes(slotElementId)) {
        renderedSlots.current.push(slot);
      }
    }
  }, []);
  const removeFromRenderedSlots = useCallback(slot => {
    if (renderedSlots && renderedSlots.current && slot) {
      renderedSlots.current = renderedSlots.current.filter(x => x !== slot);
    }
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.googletag = window.googletag || {};
      window.googletag.cmd = window.googletag.cmd || [];
      window.pbjs = window.pbjs || {};
      window.pbjs.que = window.pbjs.que || [];
    }
    if (adUnit) {
      if (!loadGPT) {
        return;
      }
      initGPT();
      if (enableAdLibrariesLoading && !isAdsScriptLoaded) {
        loadAdLibraries();
        pubmaticFirstInject = true;
      } else if (headerBidding) {
        window.googletag.cmd.push(() => {
          APSUtils.init();
        });
      }
    }
  }, [adUnit, enableAdLibrariesLoading, headerBidding, initGPT, isAdsScriptLoaded, loadAdLibraries, loadGPT]);
  useEffect(() => {
    let pubads = null;
    const addSlotRenderListener = () => {
      if (window.googletag) {
        window.googletag.cmd.push(() => {
          pubads = window.googletag.pubads();
          pubads.addEventListener('slotRenderEnded', handleSlotRenderEnded);
        });
      }
    };
    addSlotRenderListener();
    return () => {
      if (pubads) {
        pubads.removeEventListener('slotRenderEnded', handleSlotRenderEnded);
      }
    };
  }, [handleSlotRenderEnded]);
  useEffect(() => {
    // deep comparison of pageLevelTargeting objects
    const isPageLevelTargetingChanged = oldPageLevelTargetingProp && !_isEqual(pageLevelTargeting, oldPageLevelTargetingProp);
    const isPageLevelTargetingReady = !oldPageLevelTargetingProp && pageLevelTargeting;
    const differencesInTargeting = checkTargetingDifference(oldPageLevelTargetingProp, pageLevelTargeting);
    if (isPageLevelTargetingReady || isPageLevelTargetingChanged) {
      window.googletag.cmd.push(() => {
        getPageLevelTargeting(pageLevelTargeting);
      });
      let isChangeRefreshAds;
      if (!targetingChangeWithoutRefreshAds || targetingChangeWithoutRefreshAds.length === 0) {
        isChangeRefreshAds = true;
      } else {
        isChangeRefreshAds = !Object.keys(differencesInTargeting).every(key => targetingChangeWithoutRefreshAds.includes(key));
      }
      if (isChangeRefreshAds) {
        if (renderedSlots.current.length) {
          fetchBidsAndRefreshSlots(renderedSlots.current);
        }
        renderedLazySlots.current = [];
      }
    }
  });
  const providerValue = useMemo(() => ({
    addAdSlot,
    addUnRefreshedAdCount,
    refreshAds,
    clearAdSlots,
    adUnit,
    addToLazyLoadedAds,
    addToRenderedSlots,
    removeFromRenderedSlots,
    prebidAdapters,
    isAdsScriptLoaded
  }), [addAdSlot, addUnRefreshedAdCount, refreshAds, clearAdSlots, adUnit, addToLazyLoadedAds, addToRenderedSlots, removeFromRenderedSlots, prebidAdapters, isAdsScriptLoaded]);
  return /*#__PURE__*/_jsx(DFPContext.Provider, {
    value: providerValue
  }, void 0, children);
}
export default /*#__PURE__*/memo(DFPProvider);